import React, { useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { ToolsPanel } from './components/ToolsPanel';
import { createPortal } from 'react-dom';
import { EASY_EMAIL_EDITOR_ID, FIXED_CONTAINER_ID } from '@/constants';
import { EditEmailPreview } from './components/EditEmailPreview';
import { PreviewModal } from './components/PreviewModal';
import { ReconciliationNotice } from './components/ReconciliationNotice';
import { useEditorProps } from '@/hooks/useEditorProps';
import { useBlock } from '@/hooks/useBlock';

import './index.scss';
import './overlay-scrollbars-override.css';
import '@/assets/font/iconfont.css';

// react-codemirror compatibility
(window as Window & typeof globalThis & { global: Window }).global = window;

export interface EmailEditorRef {
	openPreview: () => void;
	hasUnsavedChanges: () => boolean;
}

export interface EmailEditorProps {
	onSendTestEmail?: (emails: string[], html: string, subject: string) => Promise<void>;
	userEmail?: string;
	userName?: string;
	userId?: string | number | null;
	onSubjectPreviewSave?: (subject: string, previewText: string) => void;
	openFromModal?: boolean;
}

export const EmailEditor = forwardRef<EmailEditorRef, EmailEditorProps>((props, ref) => {
	const {
		onSendTestEmail,
		userEmail,
		userName,
		userId,
		onSubjectPreviewSave,
		openFromModal = false,
	} = props;
	const { height: containerHeight } = useEditorProps();
	const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
	const { undoable } = useBlock();

	const fixedContainer = useMemo(() => {
		return createPortal(<div id={FIXED_CONTAINER_ID} />, document.body);
	}, []);

	// Expose methods via ref
	useImperativeHandle(ref, () => {
		const refObject = {
			openPreview: () => {
				setIsPreviewModalVisible(true);
			},
			hasUnsavedChanges: () => {
				return undoable;
			},
		};
		return refObject;
	}, [undoable]);

	return useMemo(
		() => (
			<div
				id={EASY_EMAIL_EDITOR_ID}
				data-email-editor-root='true'
				style={{
					display: 'flex',
					flex: '1',
					flexDirection: 'column',
					overflow: 'hidden',
					justifyContent: 'center',
					minWidth: 640,
					height: containerHeight,
					position: 'relative',
					isolation: 'isolate', // Create a new stacking context
				}}
			>
				{/* Tools Panel - Fixed at top */}
				<div
					style={{
						position: 'relative',
						zIndex: 200,
					}}
				>
					<ToolsPanel />
				</div>

				<ReconciliationNotice />

				{/* Edit Mode Only - No tabs */}
				<div
					style={{
						flex: 1,
						overflow: 'hidden',
						position: 'relative',
					}}
				>
					<EditEmailPreview
						onSubjectPreviewSave={onSubjectPreviewSave}
						openFromModal={openFromModal}
					/>
				</div>

				<>{fixedContainer}</>
				<PreviewModal
					isVisible={isPreviewModalVisible}
					onClose={() => setIsPreviewModalVisible(false)}
					onSendTestEmail={onSendTestEmail}
					userEmail={userEmail}
					userName={userName}
					userId={userId}
				/>
			</div>
		),
		[
			containerHeight,
			fixedContainer,
			isPreviewModalVisible,
			onSendTestEmail,
			userEmail,
			userName,
			userId,
			onSubjectPreviewSave,
			openFromModal,
		],
	);
});

EmailEditor.displayName = 'EmailEditor';
